import type { IconType } from 'react-icons';
import {
  FiActivity,
  FiAnchor,
  FiCreditCard,
  FiMail,
  FiMessageSquare,
  FiSmartphone,
  FiUsers,
} from 'react-icons/fi';

import { NotificationCategoryType } from '@/store/notification-categories/notification-categories.types';
import { NotificationDeliveryOption } from '@/store/notification-delivery/notification-delivery.types';
import { NotificationMethodType } from '@/store/notification-settings/notification-settings.types';

export const CATEGORY_PARAM = 'category';

export const CHANNEL_ORDER = [
  NotificationMethodType.EMAIL,
  NotificationMethodType.PHONE,
  NotificationMethodType.PUSH,
] as const;

export type ChannelMeta = {
  label: string;
  icon: IconType;
  editable: boolean;
};

export const CHANNEL_META: Record<NotificationMethodType, ChannelMeta> = {
  [NotificationMethodType.EMAIL]: {
    label: 'Email',
    icon: FiMail,
    editable: true,
  },
  [NotificationMethodType.PHONE]: {
    label: 'SMS',
    icon: FiMessageSquare,
    editable: true,
  },
  [NotificationMethodType.PUSH]: {
    label: 'Push',
    icon: FiSmartphone,
    editable: false,
  },
};

export const DELIVERY_OPTIONS: { id: NotificationDeliveryOption; title: string; sub: string }[] = [
  {
    id: NotificationDeliveryOption.INSTANT,
    title: 'Instant',
    sub: 'Deliver each alert as it happens.',
  },
  {
    id: NotificationDeliveryOption.HOURLY,
    title: 'Hourly digest',
    sub: 'Bundle alerts and send once an hour.',
  },
  {
    id: NotificationDeliveryOption.DAILY,
    title: 'Daily digest',
    sub: 'One summary every morning.',
  },
];

/**
 * Tab identity in the `?category=` URL param. Most tabs are a whole backend notification
 * category; `subscription` is a curated slice of the `user` category kept on its own tab
 * because plan/billing alerts read very differently from account alerts.
 */
export type AlertGroupId = NotificationCategoryType | 'subscription';

export type AlertCategory = {
  id: AlertGroupId;
  title: string;
  sub: string;
  /** The subtitle when payments are hidden, for a category whose `sub` mentions them. */
  subWithoutPayments?: string;
  icon: IconType;
  events: { key: string; label: string }[];
};

export const ALERT_CATEGORIES: AlertCategory[] = [
  {
    id: NotificationCategoryType.CHAT,
    title: 'Chat',
    sub: 'Messages and chat membership.',
    icon: FiMessageSquare,
    events: [
      { key: 'new_message', label: 'New message received' },
      { key: 'mention_message', label: 'Mentioned in a message' },
      { key: 'assign_user_personal', label: 'Added to a chat' },
      { key: 'unassign_user_personal', label: 'Removed from a chat' },
    ],
  },
  {
    id: NotificationCategoryType.VESSEL,
    title: 'Vessel',
    sub: 'Fleet changes and service provider updates.',
    icon: FiAnchor,
    events: [
      { key: 'delete_vessel', label: 'Vessel was deleted' },
      { key: 'delete_service_provider', label: 'Service provider removed' },
      { key: 'menu_updated', label: 'Event menu was changed' },
    ],
  },
  {
    id: NotificationCategoryType.ACTIVITY,
    title: 'Activity',
    sub: 'Tasks, events and invitations.',
    icon: FiActivity,
    events: [
      { key: 'task_reminder', label: 'Task due soon' },
      { key: 'added_as_participant_task', label: 'Added to a task' },
      { key: 'added_as_responsible', label: 'Set as task owner' },
      { key: 'task_updated', label: 'Task was updated' },
      { key: 'task_deleted', label: 'Task was deleted' },
      { key: 'removed_from_task', label: 'Removed from a task' },
      { key: 'repeatable_tasks_created', label: 'Recurring tasks were scheduled' },
      { key: 'upcoming_event', label: 'Event coming up' },
      { key: 'added_as_participant_event', label: 'Added to an event' },
      { key: 'event_updated', label: 'Event was updated' },
      { key: 'event_canceled', label: 'Event was canceled' },
      { key: 'event_renewed', label: 'Canceled event is back on' },
      { key: 'removed_from_event', label: 'Removed from an event' },
      { key: 'invite_created', label: 'You received an event invite' },
      { key: 'invite_declined', label: 'Someone declined your invite' },
      { key: 'invite_canceled', label: 'An invite you received was canceled' },
    ],
  },
  {
    id: NotificationCategoryType.USER,
    title: 'User',
    sub: 'Roles, permissions, payments and account alerts.',
    subWithoutPayments: 'Roles, permissions and account alerts',
    icon: FiUsers,
    events: [
      { key: 'roles_assigned', label: 'Your permissions were expanded' },
      { key: 'roles_removed', label: 'Your permissions were reduced' },
      { key: 'ownership_transferred', label: 'Vessel ownership was handed over' },
      { key: 'vessel_owner_left', label: 'The owner left a vessel you captain' },
      { key: 'crew_contract_expiring', label: "A crew member's temporary contract ends soon" },
      { key: 'crew_contract_ended', label: "A crew member's temporary contract ended" },
      { key: 'crew_contract_updated', label: 'Your temporary contract was extended or changed' },
      { key: 'crew_contract_converted', label: 'Your contract is no longer temporary' },
      {
        key: 'crew_contract_scheduled',
        label: "You accepted a temporary contract that hasn't started yet",
      },
      { key: 'referral_bonus_applied', label: 'Someone joined using your referral link' },
      { key: 'failed_to_send_email', label: 'Email could not be delivered' },
      { key: 'payment_completed', label: 'Payment went through' },
      { key: 'payment_failed', label: 'Payment failed' },
      { key: 'payment_canceled', label: 'Payment was canceled at checkout' },
      { key: 'payment_needs_approval', label: 'Payment needs your approval' },
      { key: 'payout_link_created', label: 'Payout link is ready' },
      { key: 'payout_expired', label: 'Payout link expired' },
      { key: 'payout_failed', label: 'Payout failed' },
    ],
  },
  {
    id: 'subscription',
    title: 'Account',
    sub: 'Plan, trial and status.',
    icon: FiCreditCard,
    events: [
      { key: 'subscription_activated', label: 'Subscription became active' },
      { key: 'subscription_updated', label: 'Subscription plan changed' },
      { key: 'subscription_trial_granted', label: 'Trial started' },
      // Push only by default on the backend (`inAppOnlyEventTypes`); stripe sends its own email.
      { key: 'subscription_trial_ending', label: 'Trial is ending soon' },
      { key: 'subscription_payment_failed', label: 'Subscription payment failed' },
      { key: 'subscription_canceled', label: 'Subscription was canceled' },
      { key: 'subscription_resumed', label: 'Subscription was resumed' },
      { key: 'subscription_downgraded', label: 'Subscription ended and dropped to free' },
      { key: 'subscription_payer_left', label: 'The person paying for a plan left the vessel' },
      { key: 'co_owner_seat_unavailable', label: 'A co-owner has no management seat' },
      { key: 'seat_payment_failed', label: "A seat for someone joining couldn't be bought" },
    ],
  },
];

const isPaymentEvent = (key: string) => key.startsWith('payment_') || key.startsWith('payout_');

/** The categories as a person sees them: without payment and payout events unless payments are on. */
export const alertCategoriesFor = (canPay: boolean): AlertCategory[] =>
  canPay
    ? ALERT_CATEGORIES
    : ALERT_CATEGORIES.map((cat) => ({
        ...cat,
        sub: cat.subWithoutPayments ?? cat.sub,
        events: cat.events.filter((ev) => !isPaymentEvent(ev.key)),
      }));
